import React, { useState, lazy } from 'react';
import { Shield, CheckCircle, Clock, DollarSign, ClipboardCheck, Calculator, Banknote, Sparkles } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '../components/Button';
import { MultiStepForm } from '../components/MultiStepForm';
import { WealthAccessSection } from '../components/WealthAccessSection';
import { ProcessSteps } from '../components/ProcessSteps';
import { FAQAccordion } from '../components/FAQAccordion';
import { CalculatorCTA } from '../components/CalculatorCTA';
import { GoogleReviews } from '../components/GoogleReviews';
export function HomePage() {
  const [formStep, setFormStep] = useState(1);
  const [loanResult, setLoanResult] = useState<{
    loanAmount: number;
    homeValue: string;
  } | null>(null);
  const formatCurrency = (value: string | number) => {
    const num = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : value;
    if (isNaN(num)) return '$0';
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
      maximumFractionDigits: 0
    }).format(num);
  };
  return <main className="w-full" data-id="element-517">
      {/* Hero Section */}
      <section className="w-full bg-white py-16 md:py-24 px-4" data-id="element-518">
        <div className="max-w-7xl mx-auto" data-id="element-519">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center" data-id="element-520">
            {/* Left Column: Content */}
            <div className="text-center lg:text-left" data-id="element-521">
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5
            }} data-id="element-522">
                <span className="inline-flex items-center gap-2 px-5 py-2.5 font-medium rounded-full mb-6 border border-accent/30 text-[13px] font-[Poppins,_sans-serif] text-gray-700 bg-white shadow-sm" data-id="element-523">
                  <Shield className="w-4 h-4 text-accent" data-id="element-524" />
                  Trusted by Canadian homeowners
                </span>
              </motion.div>

              <motion.h1 initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="text-5xl md:text-6xl lg:text-7xl font-bold text-gray-900 mb-6 leading-[1.1]" data-id="element-525">
                Unlock the <span className="text-accent" data-id="element-526">Power</span> of Your
                Home's Equity
              </motion.h1>

              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.3
            }} className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-4 mb-8" data-id="element-527">
                <Button variant="primary" size="lg" icon href="#form" data-id="element-528">
                  Get Started
                </Button>
                <Button variant="outline" size="lg" href="#how-it-works" data-id="element-529">
                  How It Works
                </Button>
              </motion.div>

              {/* Benefit Checkmarks */}
              <motion.div initial={{
              opacity: 0,
              y: 20
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.4
            }} className="flex flex-col sm:flex-row items-center justify-center lg:justify-start gap-6 text-sm" data-id="element-530">
                <div className="flex items-center gap-2" data-id="element-531">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" data-id="element-532" />
                  <span className="text-gray-600 font-medium" data-id="element-533">
                    Same-day approvals
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-534">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" data-id="element-535" />
                  <span className="text-gray-600 font-medium" data-id="element-536">
                    Fast digital application
                  </span>
                </div>
                <div className="flex items-center gap-2" data-id="element-537">
                  <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" data-id="element-538" />
                  <span className="text-gray-600 font-medium" data-id="element-539">
                    Up to 80% LTV
                  </span>
                </div>
              </motion.div>
            </div>

            {/* Right Column: Image with Floating Stat Cards */}
            <motion.div initial={{
            opacity: 0,
            scale: 0.95
          }} animate={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.6,
            delay: 0.2
          }} className="relative" data-id="element-540">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl" data-id="element-541">
                <img src="https://cdn.magicpatterns.com/uploads/fV8kuWmy64dtuARccNhxTD/image.png" alt="Beautiful modern home" className="w-full h-auto object-cover" loading="lazy" decoding="async" data-id="element-542" />
              </div>

              {/* Floating Stat Card 1 - Top Right */}
              <motion.div initial={{
              opacity: 0,
              y: -30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.5
            }} className="hidden md:block absolute -top-4 -right-4 bg-white rounded-xl shadow-lg p-4 w-56" data-id="element-543">
                <div className="flex items-center gap-3" data-id="element-544">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-545">
                    <Clock className="w-5 h-5 text-accent" data-id="element-546" />
                  </div>
                  <div data-id="element-547">
                    <p className="text-xs text-gray-500 font-medium" data-id="element-548">
                      Avg. Approval Time
                    </p>
                    <p className="text-2xl font-extrabold text-accent" data-id="element-549">
                      24 hrs
                    </p>
                  </div>
                </div>
              </motion.div>

              {/* Floating Stat Card 2 - Bottom Left */}
              <motion.div initial={{
              opacity: 0,
              y: 30
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.6,
              delay: 0.6
            }} className="hidden md:block absolute -bottom-4 -left-4 bg-white rounded-xl shadow-lg p-4 w-56" data-id="element-550">
                <div className="flex items-center gap-3" data-id="element-551">
                  <div className="w-10 h-10 bg-accent/10 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-552">
                    <DollarSign className="w-5 h-5 text-accent" data-id="element-553" />
                  </div>
                  <div data-id="element-554">
                    <p className="text-xs text-gray-500 font-medium" data-id="element-555">
                      Average Funding
                    </p>
                    <p className="text-2xl font-extrabold text-accent" data-id="element-556">
                      $185,000
                    </p>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-5xl mx-auto px-4" data-id="element-557">
        <hr className="border-gray-200" data-id="element-558" />
      </div>

      {/* Form Section - "How Much Can You Borrow" */}
      <section id="form" className="w-full bg-cream py-12 md:py-16 px-4" data-id="element-559">
        <div className="max-w-7xl mx-auto" data-id="element-560">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-start" data-id="element-561">
            {/* Left Column: Text + Result */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5
          }} className="lg:sticky lg:top-28 text-center lg:text-left pt-4" data-id="element-562">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4" data-id="element-563">
                Find Out How Much You Can Borrow
              </h2>
              <p className="text-lg text-gray-600 mb-6" data-id="element-564">
                Get your free assessment in less than 60 seconds
              </p>

              {/* Calculation Result Card - shown when step >= 2 */}
              <AnimatePresence mode="wait" data-id="element-565">
                {formStep >= 2 && loanResult && <motion.div key="result-card" initial={{
                opacity: 0,
                y: 20,
                scale: 0.95
              }} animate={{
                opacity: 1,
                y: 0,
                scale: 1
              }} exit={{
                opacity: 0,
                y: -10,
                scale: 0.95
              }} transition={{
                duration: 0.5,
                ease: 'easeOut'
              }} className="mt-2" data-id="element-566">
                    <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 text-center" data-id="element-567">
                      <div className="flex items-center justify-center gap-2 mb-4" data-id="element-568">
                        <Sparkles className="w-5 h-5 text-accent" data-id="element-569" />
                        <span className="text-sm font-semibold text-gray-500 uppercase tracking-wider" data-id="element-570">
                          Estimated Available Equity
                        </span>
                      </div>
                      <div className="text-5xl md:text-6xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-accent to-yellow-600 mb-6 tracking-tight py-1" data-id="element-571">
                        {formatCurrency(loanResult.loanAmount)}
                      </div>
                      <div className="flex items-center justify-center gap-6 text-sm" data-id="element-572">
                        <div className="text-center" data-id="element-573">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1" data-id="element-574">
                            Home Value
                          </p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-575">
                            {formatCurrency(loanResult.homeValue)}
                          </p>
                        </div>
                        <div className="w-px h-10 bg-gray-200" data-id="element-576" />
                        <div className="text-center" data-id="element-577">
                          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1" data-id="element-578">
                            Max LTV
                          </p>
                          <p className="text-lg font-bold text-gray-900" data-id="element-579">80%</p>
                        </div>
                      </div>
                    </div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>

            {/* Right Column: Form */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} data-id="element-580">
              <MultiStepForm onStepChange={(step, data) => {
              setFormStep(step);
              if (data) {
                setLoanResult(data);
              }
            }} data-id="element-581" />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Divider */}
      <div className="max-w-5xl mx-auto px-4" data-id="element-582">
        <hr className="border-gray-200" data-id="element-583" />
      </div>

      {/* How It Works - 3 Step Process */}
      <section id="how-it-works" className="w-full bg-white py-20 px-4" data-id="element-584">
        <div className="max-w-7xl mx-auto" data-id="element-585">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5
        }} className="text-center mb-16" data-id="element-586">
            <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 mb-4" data-id="element-587">
              How It Works
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto" data-id="element-588">
              Get funded in three simple steps
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-12 relative" data-id="element-589">
            {/* Step 1 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.1
          }} className="relative" data-id="element-590">
              <div className="bg-white rounded-xl shadow-sm p-8 text-center h-full border border-gray-100 hover:shadow-md transition-shadow" data-id="element-591">
                <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-6" data-id="element-592">
                  <ClipboardCheck className="w-8 h-8 text-primary" data-id="element-593" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-594">
                  Check Your Eligibility
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-595">
                  Answer a few quick questions about your property to see if you
                  qualify
                </p>
              </div>
            </motion.div>

            {/* Step 2 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.2
          }} className="relative" data-id="element-596">
              <div className="bg-white rounded-xl shadow-sm p-8 text-center h-full border border-gray-100 hover:shadow-md transition-shadow" data-id="element-597">
                <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-6" data-id="element-598">
                  <Calculator className="w-8 h-8 text-primary" data-id="element-599" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-600">
                  Get Your Funding Amount
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-601">
                  We'll assess your home's equity and present you with a
                  personalized offer
                </p>
              </div>
            </motion.div>

            {/* Step 3 */}
            <motion.div initial={{
            opacity: 0,
            y: 30
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.5,
            delay: 0.3
          }} className="relative" data-id="element-602">
              <div className="bg-white rounded-xl shadow-sm p-8 text-center h-full border border-gray-100 hover:shadow-md transition-shadow" data-id="element-603">
                <div className="w-16 h-16 bg-primary/10 rounded-xl flex items-center justify-center mx-auto mb-6" data-id="element-604">
                  <Banknote className="w-8 h-8 text-primary" data-id="element-605" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3" data-id="element-606">
                  Receive Your Funds
                </h3>
                <p className="text-gray-600 leading-relaxed" data-id="element-607">
                  Once approved, funds are deposited directly into your lawyer's
                  trust account
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Wealth Access Section */}
      <WealthAccessSection data-id="element-608" />

      {/* Process Steps */}
      <ProcessSteps data-id="element-609" />

      {/* Calculator CTA Section */}
      <CalculatorCTA data-id="element-610" />

      {/* Google Reviews Section */}
      <GoogleReviews data-id="element-611" />

      {/* FAQ Section */}
      <FAQAccordion data-id="element-612" />
    </main>;
}